import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { MoneyText } from "@redotech/redo-web/fx/money-text";
import { toMoney } from "@redotech/redo-web/fx/to-money";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { OrderFulfillmentAddress } from "../../common/order-fulfillment-address";

export const FulfillmentOrderInformationCard = memo(
  function FulfillmentOrderInformationCard({
    fulfillmentGroup,
  }: {
    fulfillmentGroup: FulfillmentGroupData;
  }) {
    const totalTax = useMemo(() => {
      return Number(fulfillmentGroup.totalTax.amount);
    }, [fulfillmentGroup.totalTax]);

    const totalItemsPrice = useMemo(() => {
      return Number(fulfillmentGroup.totalPrice.amount);
    }, [fulfillmentGroup.totalPrice]);

    const totalDiscounts = useMemo(() => {
      return (
        Number(fulfillmentGroup.totalPrice.amount) -
        Number(
          fulfillmentGroup.totalDiscountedPrice?.amount ??
            fulfillmentGroup.totalPrice.amount,
        )
      );
    }, [
      fulfillmentGroup.totalDiscountedPrice,
      fulfillmentGroup.totalPrice.amount,
    ]);

    const customerShippingPaid = useMemo(() => {
      return Number(fulfillmentGroup.customerShippingPaid ?? 0);
    }, [fulfillmentGroup.customerShippingPaid]);

    const totalOrderPrice = useMemo(() => {
      return totalItemsPrice + totalTax + customerShippingPaid - totalDiscounts;
    }, [totalItemsPrice, totalTax, totalDiscounts, customerShippingPaid]);

    const landedCost = fulfillmentGroup.landedCost;

    // Landed cost is baked into the paid shipping charge; subtract it back out so
    // Shipping shows the actual charge and landed-cost rows show the rest.
    const shippingExcludingLandedCost = useMemo(() => {
      const landedCostTotal = landedCost
        ? landedCost.duties + landedCost.taxes + landedCost.fees
        : 0;
      return Math.max(0, customerShippingPaid - landedCostTotal);
    }, [customerShippingPaid, landedCost]);

    const billingAddress = fulfillmentGroup.billingAddress;

    return (
      <Flex dir="column" gap="2xl">
        {billingAddress && (
          <OrderFulfillmentAddress
            displayAddress={billingAddress}
            fontWeight="medium"
            hideEditButton
            originalAddress={billingAddress}
            titleText="Billing address"
          />
        )}
        <Flex dir="column" gap="xxs">
          <Flex dir="row" justify="space-between">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              Products
            </Text>
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              <MoneyText
                struck
                value={toMoney({
                  amount: totalItemsPrice,
                  currency: fulfillmentGroup.totalPrice.currency,
                })}
              />
            </Text>
          </Flex>
          {totalDiscounts > 0 && (
            <Flex dir="row" justify="space-between">
              <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                Discounts
              </Text>
              <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                -
                <MoneyText
                  struck
                  value={toMoney({
                    amount: totalDiscounts,
                    currency: fulfillmentGroup.totalPrice.currency,
                  })}
                />
              </Text>
            </Flex>
          )}
          <Flex dir="row" justify="space-between">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              Estimated tax
            </Text>
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              <MoneyText
                struck
                value={toMoney({
                  amount: totalTax,
                  currency: fulfillmentGroup.totalPrice.currency,
                })}
              />
            </Text>
          </Flex>
          <Flex dir="row" justify="space-between">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              Shipping
            </Text>
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              <MoneyText
                struck
                value={toMoney({
                  amount: shippingExcludingLandedCost,
                  currency: fulfillmentGroup.totalPrice.currency,
                })}
              />
            </Text>
          </Flex>
          {landedCost && (
            <>
              <Flex dir="row" justify="space-between">
                <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                  Taxes
                </Text>
                <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                  <MoneyText
                    struck
                    value={toMoney({
                      amount: landedCost.taxes,
                      currency: fulfillmentGroup.totalPrice.currency,
                    })}
                  />
                </Text>
              </Flex>
              <Flex dir="row" justify="space-between">
                <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                  Duties and fees
                </Text>
                <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                  <MoneyText
                    struck
                    value={toMoney({
                      amount: landedCost.duties + landedCost.fees,
                      currency: fulfillmentGroup.totalPrice.currency,
                    })}
                  />
                </Text>
              </Flex>
            </>
          )}
          <Divider />
          <Flex dir="row" justify="space-between">
            <Text fontSize="xs" fontWeight="medium">
              Total
            </Text>
            <Text fontSize="xs" fontWeight="medium">
              <MoneyText
                struck
                value={toMoney({
                  amount: totalOrderPrice,
                  currency: fulfillmentGroup.totalPrice.currency,
                })}
              />
            </Text>
          </Flex>
        </Flex>
      </Flex>
    );
  },
);
